// src/pages/Aliases.tsx
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { AliasFormModal, type AliasRow } from '@/components/AliasFormModal';
import { BackButton } from '@/components/BackButton';
import { ListCard } from '@/components/ui/ListCard';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/lib/toast';

// Aliases sharing the same display_name are different SOURCE rules pointing
// at the same canonical merchant — created intentionally so different AI
// extractions ("MacDonald" / "Mcdonald's") converge on one display. The user
// reads these as "duplicates", so the UI groups them under a single card and
// lists each source as a sub-row.
interface AliasGroup {
  /** Lowercased display_name — stable group key */
  key: string;
  /** Most-applied member's display fields used for the group header */
  primary: AliasRow;
  /** All members, sorted by source_key for deterministic ordering */
  members: AliasRow[];
}

function groupByTarget(rows: AliasRow[]): AliasGroup[] {
  const map = new Map<string, AliasRow[]>();
  for (const r of rows) {
    const key = r.display_name.trim().toLowerCase();
    const bucket = map.get(key);
    if (bucket) bucket.push(r);
    else map.set(key, [r]);
  }
  const groups: AliasGroup[] = [];
  for (const [key, members] of map) {
    members.sort((a, b) => a.source_key.localeCompare(b.source_key));
    // Primary = member with the highest apply_count, falling back to most
    // recently updated. Used purely for the header's display_name_zh /
    // category — since all members share display_name by construction, the
    // header readout is identical regardless of which row we pick.
    const primary = [...members].sort(
      (a, b) => b.apply_count - a.apply_count || b.updated_at - a.updated_at,
    )[0];
    groups.push({ key, primary, members });
  }
  // Sort groups by max apply_count desc (= primary.apply_count, since live
  // count is shared across members) so most-used merchants surface first.
  groups.sort(
    (a, b) =>
      b.primary.apply_count - a.primary.apply_count ||
      b.primary.updated_at - a.primary.updated_at,
  );
  return groups;
}

interface CategoryRow {
  id: string;
  name: string;
  name_zh: string | null;
  icon: string | null;
}

function relativeKey(unix: number | null): { key: string; count: number } | null {
  if (!unix) return null;
  const diffSec = Math.max(0, Math.floor(Date.now() / 1000) - unix);
  if (diffSec < 60) return { key: 'aliases.lastAppliedJustNow', count: 0 };
  if (diffSec < 3600) return { key: 'aliases.lastAppliedMinutesAgo', count: Math.floor(diffSec / 60) };
  if (diffSec < 86400) return { key: 'aliases.lastAppliedHoursAgo', count: Math.floor(diffSec / 3600) };
  if (diffSec < 86400 * 30) return { key: 'aliases.lastAppliedDaysAgo', count: Math.floor(diffSec / 86400) };
  if (diffSec < 86400 * 365) return { key: 'aliases.lastAppliedMonthsAgo', count: Math.floor(diffSec / (86400 * 30)) };
  return { key: 'aliases.lastAppliedYearsAgo', count: Math.floor(diffSec / (86400 * 365)) };
}

export function AliasesPage() {
  const { t } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const qc = useQueryClient();
  const location = useLocation();

  const { data: aliases, isLoading } = useQuery({
    queryKey: ['aliases', ws?.id],
    queryFn: () => api.get<AliasRow[]>('/aliases'),
    enabled: !!ws,
  });

  const { data: categories } = useQuery({
    queryKey: ['categories', ws?.id],
    queryFn: () => api.get<CategoryRow[]>('/categories'),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; row: AliasRow } | null>(
    null,
  );

  // Allow deep-linking from a receipt-detail chip: /aliases#alias-<id>.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  useEffect(() => {
    const m = location.hash.match(/^#alias-(.+)$/);
    if (m) setHighlightId(m[1]);
  }, [location.hash]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/aliases/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aliases', ws?.id] });
      toast.success(t('common.deleted'));
    },
    onError: (err) =>
      toast.error(
        t('common.deleteFailed', { error: err instanceof Error ? err.message : '?' }),
      ),
  });

  const categoryById = (id: string | null) =>
    id ? categories?.find((c) => c.id === id) ?? null : null;

  const groups = useMemo(() => groupByTarget(aliases ?? []), [aliases]);

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-background">
        <div className="container flex items-center justify-between gap-3 py-4">
          <div className="flex items-center gap-3">
            <BackButton to="/settings/data" />
            <div>
              <h1 className="text-lg font-semibold">{t('aliases.title')}</h1>
              <p className="text-xs text-muted-foreground">{t('aliases.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => setModal({ mode: 'create' })}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            {t('aliases.addNew')}
          </button>
        </div>
      </header>

      <main className="container max-w-3xl py-6">
        {isLoading && <p className="text-sm text-muted-foreground">{t('aliases.loading')}</p>}

        <ListCard
          icon="🔗"
          title={t('aliases.title')}
          summary={
            aliases && aliases.length > 0
              ? t('aliases.totalCount', { count: aliases.length })
              : undefined
          }
          isEmpty={!isLoading && (aliases?.length ?? 0) === 0}
          emptyState={
            <div>
              <p className="text-sm">{t('aliases.empty')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('aliases.emptyHint')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('aliases.emptyHintEn')}</p>
            </div>
          }
        >
          {groups.map((g) => {
            const cat = categoryById(g.primary.default_category_id);
            // Highlight whole group if hash-deep-link target lives inside it.
            const highlighted = g.members.some((m) => m.id === highlightId);
            // Use the most-recent last_applied_at across members for the
            // group footer — apply_count is identical (computed live from
            // shared display_name), so picking any one row is the same.
            const latestApplied = g.members.reduce<number | null>(
              (acc, m) =>
                m.last_applied_at && (acc == null || m.last_applied_at > acc)
                  ? m.last_applied_at
                  : acc,
              null,
            );
            const groupId = g.members[0].id;
            return (
              <div
                key={g.key}
                id={`alias-${groupId}`}
                className={
                  'py-3 ' +
                  (highlighted ? 'rounded-lg ring-2 ring-primary/40 -mx-2 px-2' : '')
                }
              >
                {/* Group header — the canonical target. ✏️ / 🗑 operate on
                    the primary member so single-source groups behave exactly
                    like before; multi-source groups still need each source
                    listed below for per-source edit/delete. */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-base font-semibold">{g.primary.display_name}</span>
                      {g.primary.display_name_zh && (
                        <span className="text-sm text-muted-foreground">
                          / {g.primary.display_name_zh}
                        </span>
                      )}
                      {g.members.length > 1 && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {t('aliases.variantCount', { count: g.members.length })}
                        </span>
                      )}
                    </div>
                    {cat && (
                      <div className="text-xs text-muted-foreground">
                        {cat.icon ? cat.icon + ' ' : ''}
                        {cat.name}
                        {cat.name_zh ? ` / ${cat.name_zh}` : ''}
                      </div>
                    )}
                  </div>
                  {g.members.length === 1 && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => setModal({ mode: 'edit', row: g.primary })}
                        className="flex h-11 w-11 items-center justify-center rounded-md border text-xs hover:bg-muted"
                        aria-label={t('common.edit')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={async () => {
                          if (await confirmDialog({ title: t('aliases.deleteConfirm'), destructive: true })) {
                            deleteMutation.mutate(g.primary.id);
                          }
                        }}
                        className="flex h-11 w-11 items-center justify-center rounded-md border text-xs hover:bg-muted"
                        aria-label={t('common.delete')}
                      >
                        🗑
                      </button>
                    </div>
                  )}
                </div>

                {/* Source variant rows — only render when group has 2+ so
                    single-source groups stay visually identical to pre-0.2.31. */}
                {g.members.length > 1 && (
                  <ul className="mt-2 space-y-1 rounded-md border bg-background/60 p-2">
                    {g.members.map((m) => (
                      <li
                        key={m.id}
                        id={`alias-${m.id}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="min-w-0 flex-1 line-clamp-2 break-all font-mono text-xs text-muted-foreground">
                          {m.source_key}
                        </span>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            onClick={() => setModal({ mode: 'edit', row: m })}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border text-xs hover:bg-muted"
                            aria-label={t('common.edit')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={async () => {
                              if (await confirmDialog({ title: t('aliases.deleteConfirm'), destructive: true })) {
                                deleteMutation.mutate(m.id);
                              }
                            }}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border text-xs hover:bg-muted"
                            aria-label={t('common.delete')}
                          >
                            🗑
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Single-source groups: show the source_key on its own line
                    so layout still reveals what the rule matches against. */}
                {g.members.length === 1 && (
                  <div className="mt-1 font-mono text-xs text-muted-foreground">
                    {g.primary.source_key}
                  </div>
                )}

                <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                  <span>{t('aliases.appliedNTimes', { count: g.primary.apply_count })}</span>
                  <span>
                    {(() => {
                      const r = relativeKey(latestApplied);
                      return r ? t(r.key, { count: r.count }) : t('aliases.lastAppliedNever');
                    })()}
                  </span>
                </div>
              </div>
            );
          })}
        </ListCard>
      </main>

      {modal && (
        <AliasFormModal
          mode={modal.mode}
          initial={modal.mode === 'edit' ? modal.row : undefined}
          onClose={() => setModal(null)}
        />
      )}

    </div>
  );
}
